/**
 * The tenant endpoints under `admin/tenants`. Every one requires a staff access
 * token — the API's guard rejects a tenant token on these paths before any
 * handler runs, and `@AdminOnly()` sits on the controller class rather than on
 * each route so a new route is protected by default rather than by memory.
 *
 * Request and response shapes come from `@wend/shared`. Nothing is re-declared
 * here, and nothing here decides what a valid request looks like.
 *
 * SLICE 8 CHANGED WHAT THIS MODULE IS FOR, so the header says so rather than
 * being left standing. It used to open "the two tenant endpoints slice 0
 * exercises" — create and list — and that sentence quietly asserted that a
 * tenant, once created, was fixed. It was: there was no `PATCH`, and a
 * misspelled workspace name or a wrong contact address could only be corrected
 * in the database by hand. {@link updateTenant} is that gap closed.
 */
import {
  createTenantRequestSchema,
  createTenantResponseSchema,
  tenantListResponseSchema,
  tenantResponseSchema,
  tenantUsageResponseSchema,
  updateTenantRequestSchema,
  updateTenantResponseSchema,
  type CreateTenantRequest,
  type CreateTenantResponse,
  type TenantListResponse,
  type TenantResponse,
  type TenantUsageResponse,
  type UpdateTenantRequest,
  type UpdateTenantResponse,
} from '@wend/shared';

import { TENANTS_PATH, tenantPath, tenantUsagePath } from './admin-endpoints';
import { apiClient } from './api-client';

export interface TenantListParams {
  readonly page: number;
  readonly pageSize: number;
  /** Matches workspace name or contact email, case-insensitive. */
  readonly search?: string;
}

export async function listTenants(
  params: TenantListParams,
  signal?: AbortSignal,
): Promise<TenantListResponse> {
  return apiClient.request({
    path: TENANTS_PATH,
    schema: tenantListResponseSchema,
    query: {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
    },
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Provisions the workspace, its feature flags and its owner, then the API
 * emails the owner a single-use set-password link.
 *
 * `input` is already the *output* of `createTenantRequestSchema` — the caller
 * parses the form with the shared contract, so the request cannot be malformed
 * by the time it gets here. The response carries the link's expiry and
 * deliberately never the token or a password.
 */
export async function createTenant(input: CreateTenantRequest): Promise<CreateTenantResponse> {
  return apiClient.request({
    path: TENANTS_PATH,
    method: 'POST',
    // Parse once more so a body that drifted from the contract fails here,
    // in this app, rather than as a 400 from the API.
    body: createTenantRequestSchema.parse(input),
    schema: createTenantResponseSchema,
  });
}

/** One workspace, in full. The read shape the detail screen is drawn from. */
export async function getTenant(tenantId: string, signal?: AbortSignal): Promise<TenantResponse> {
  return apiClient.request({
    path: tenantPath(tenantId),
    schema: tenantResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Corrects a tenant's details, its feature set or its subscription expiry.
 *
 * `input.changes` carries **only** what staff may change, because
 * `tenantEditableFieldsSchema` is `.strict()`: a body naming `subscriptionKey`
 * fails parsing on the line below, in this app, rather than being silently
 * stripped and answered `200` by an API that did not make the change asked for.
 * That refusal is the whole point of the shape, so it is exercised here as well
 * as at the API — a request this client cannot build is a request that never
 * reaches the one role in the system that has `BYPASSRLS`.
 *
 * The response's `changes` is what actually **differed from the stored row**,
 * not what the request named, and an empty array is a real answer: nothing the
 * form submitted was different from what was already there. The screen says so
 * in words rather than showing a success toast for a save that moved nothing.
 */
export async function updateTenant(
  tenantId: string,
  input: UpdateTenantRequest,
): Promise<UpdateTenantResponse> {
  return apiClient.request({
    path: tenantPath(tenantId),
    method: 'PATCH',
    body: updateTenantRequestSchema.parse(input),
    schema: updateTenantResponseSchema,
  });
}

/**
 * Slice 71: that workspace's flows by status and its rule segments, counted
 * on read. Counts only — see `tenantUsageResponseSchema`.
 */
export async function getTenantUsage(tenantId: string, signal?: AbortSignal): Promise<TenantUsageResponse> {
  return apiClient.request({
    path: tenantUsagePath(tenantId),
    schema: tenantUsageResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}
